import { Configuration, SystemApi } from '../client';

export interface SystemConfiguration {
  domains: string[];
  rootUrl: string;
  smsEnabled: boolean;
  mfaRequired: boolean;
}

type SystemStateObserver = (state: SystemState) => void;

export class SystemState {
  private static instance: SystemState;
  private observers: SystemStateObserver[] = [];
  private _isLoading: boolean = true;
  private _error: string | null = null;
  private _configuration: SystemConfiguration | null = null;

  /**
   * Get the singleton instance of SystemState
   */
  public static getInstance(): SystemState {
    if (!SystemState.instance) {
      SystemState.instance = new SystemState();
    }
    return SystemState.instance;
  }

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Initialize the system state by loading configuration from the server
   * @param config API configuration
   */
  public async initialize(config: Configuration): Promise<void> {
    if (!this._isLoading && this._configuration) {
      return; // Already initialized
    }

    this._isLoading = true;
    this._error = null;
    this.notifyObservers();

    try {
      // Create SystemApi instance with the provided configuration
      const systemApi = new SystemApi(config);

      // Use the generated client to get system configuration
      const response = await systemApi.apiSystemConfigurationGet();

      this._configuration = {
        domains: response.data.domains || [],
        rootUrl: response.data.rootUrl || '',
        smsEnabled: response.data.smsEnabled ?? false,
        mfaRequired: response.data.mfaRequired ?? false,
      };
      this._isLoading = false;
      this.notifyObservers();
    } catch (error) {
      this._error =
        error instanceof Error
          ? error.message
          : 'Unknown error loading system configuration';
      this._isLoading = false;
      this.notifyObservers();
      console.error('Error loading system configuration:', error);
    }
  }

  /**
   * Add an observer to be notified when the system state changes
   * @param observer Function to call when state changes
   * @returns Function to remove the observer
   */
  public addObserver(observer: SystemStateObserver): () => void {
    this.observers.push(observer);

    // If we already have data, notify the observer immediately
    if (!this._isLoading) {
      observer(this);
    }

    // Return a function to remove this observer
    return () => {
      this.observers = this.observers.filter((obs) => obs !== observer);
    };
  }

  /**
   * Notify all observers of a state change
   */
  private notifyObservers(): void {
    this.observers.forEach((observer) => observer(this));
  }

  /**
   * Get the first domain from the domains array, or an empty string if not available
   */
  public get primaryDomain(): string {
    return this._configuration && this._configuration.domains.length > 0
      ? this._configuration.domains[0]
      : '';
  }

  /**
   * Generate an email address based on team name and agent name
   * @param teamName The team name
   * @param agentName The agent name
   * @returns Formatted email address or empty string if domain not available
   */
  public generateAgentEmail(teamName: string, agentName: string): string {
    if (!this.primaryDomain) {
      return '';
    }

    // Format: {team-name}+{agent-name}@{first-domain}
    // Helper function to format names consistently
    const formatName = (name: string): string => {
      return (
        name
          .toLowerCase()
          // Replace non-alphanumeric characters (except for alphanumeric and underscore) with hyphens
          .replace(/[^\w\s]/g, '-')
          // Replace spaces with hyphens
          .replace(/\s+/g, '-')
          // Replace multiple consecutive hyphens with a single hyphen
          .replace(/-+/g, '-')
          // Remove leading and trailing hyphens
          .replace(/^-+|-+$/g, '')
      );
    };

    const formattedTeamName = formatName(teamName);
    const formattedAgentName = formatName(agentName);

    return `${formattedTeamName}+${formattedAgentName}@${this.primaryDomain}`;
  }

  /**
   * Check if the system state is currently loading
   */
  public get isLoading(): boolean {
    return this._isLoading;
  }

  /**
   * Get any error that occurred during loading
   */
  public get error(): string | null {
    return this._error;
  }

  /**
   * Get the system configuration
   */
  public get configuration(): SystemConfiguration | null {
    return this._configuration;
  }

  /**
   * Get all available domains
   */
  public get domains(): string[] {
    return this._configuration ? this._configuration.domains : [];
  }

  /**
   * Get the root URL
   */
  public get rootUrl(): string {
    return this._configuration ? this._configuration.rootUrl : '';
  }

  /**
   * Whether a platform Twilio account is configured, and SMS surfaces
   * (channels + phone linking) should be shown. Defaults to false when the
   * configuration hasn't loaded yet or the server omitted the field.
   */
  public get smsEnabled(): boolean {
    return this._configuration ? this._configuration.smsEnabled : false;
  }

  public get mfaRequired(): boolean {
    return this._configuration?.mfaRequired ?? false;
  }
}
